import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TowerControl, MapPin, TrainFront, Plane, Sparkles, ExternalLink } from "lucide-react";
import api from "../lib/api";

/* Embeddable IndianAirports planner widget — iframed by partner sites via
   /india-widget.js. Bare chrome (App.js embedMode). All outbound links open the
   full site in a new tab with UTM tracking so partner-sourced traffic is measurable. */
export default function IndiaPlannerWidget() {
  const [params] = useSearchParams();
  const accent = /^#[0-9a-fA-F]{3,8}$/.test(params.get("accent") || "") ? params.get("accent") : "#1d4ed8";
  const partner = (params.get("partner") || "partner").slice(0, 60);
  const [airports, setAirports] = useState([]);
  const [iata, setIata] = useState((params.get("airport") || "").toUpperCase());

  useEffect(() => {
    document.title = "IndianAirports.com widget";
    api.get("/india/airports").then((r) => {
      const items = r.data.items || [];
      setAirports(items);
      setIata((cur) => (items.some((a) => a.iata === cur) ? cur : (items[0]?.iata || "")));
    });
  }, []);

  const a = useMemo(() => airports.find((x) => x.iata === iata), [airports, iata]);
  const utm = `utm_source=widget&utm_medium=embed&utm_campaign=${encodeURIComponent(partner)}&ref=indianairports-widget`;

  return (
    <div className="min-h-screen bg-white p-3 flex flex-col" data-testid="india-widget">
      <div className="flex items-center gap-2">
        <span className="inline-flex p-1.5 rounded-lg text-white shrink-0" style={{ background: accent }}><TowerControl className="w-4 h-4" /></span>
        <span className="font-heading font-900 text-sm leading-none"><span style={{ color: accent }}>Indian</span><span className="text-orange-600">Airports</span><span className="text-zinc-400 text-xs">.com</span></span>
        <a href={`/india/airports?${utm}`} target="_blank" rel="noopener noreferrer" data-testid="india-widget-site-link"
          className="ml-auto text-[10px] font-700 text-zinc-400 hover:text-zinc-600 inline-flex items-center gap-0.5">Open site <ExternalLink className="w-2.5 h-2.5" /></a>
      </div>

      <label className="block mt-3">
        <span className="text-[10px] font-800 uppercase text-zinc-400">Choose your airport</span>
        <select value={iata} onChange={(e) => setIata(e.target.value)} data-testid="india-widget-select"
          className="mt-1 w-full text-sm font-700 border border-zinc-300 rounded-xl px-2.5 py-2 bg-white focus:outline-none">
          {airports.map((x) => <option key={x.iata} value={x.iata}>{x.city} — {x.name} ({x.iata})</option>)}
        </select>
      </label>

      {a && (
        <div className="mt-2.5 border border-zinc-200 rounded-xl overflow-hidden" data-testid="india-widget-card">
          <div className="relative h-16">
            <img src={a.image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 to-transparent" />
            <div className="absolute bottom-1.5 left-2.5 right-2.5 text-white">
              <div className="text-xs font-800 leading-tight truncate">{a.name}</div>
              <div className="text-[10px] opacity-90 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {a.city}, {a.state} · <b className="font-mono">{a.iata}</b></div>
            </div>
          </div>
          <div className="p-2.5 flex flex-wrap items-center gap-1.5 text-[10px]">
            <span className="font-800 uppercase border rounded px-1.5 py-0.5" style={{ color: accent, borderColor: accent }}>{(a.type || "").replace("_", " ")}</span>
            {a.has_metro && <span className="inline-flex items-center gap-0.5 text-emerald-600 font-700"><TrainFront className="w-3 h-3" /> Metro</span>}
            {a.tagline && <span className="text-zinc-500 truncate">{a.tagline}</span>}
          </div>
        </div>
      )}

      <div className="mt-2.5 grid gap-1.5">
        <a href={a ? `/india/airports/${a.slug}?${utm}` : "#"} target="_blank" rel="noopener noreferrer" data-testid="india-widget-guide-btn"
          className="inline-flex items-center justify-center gap-1.5 text-white text-xs font-800 rounded-xl px-3 py-2.5" style={{ background: accent }}>
          <Plane className="w-3.5 h-3.5" /> Airport guide — terminals, tips & transport
        </a>
        <a href={`/plan-ai?${utm}${a ? `&dest=${encodeURIComponent(a.city)}` : ""}`} target="_blank" rel="noopener noreferrer" data-testid="india-widget-plan-btn"
          className="inline-flex items-center justify-center gap-1.5 text-xs font-800 rounded-xl px-3 py-2.5 border" style={{ color: accent, borderColor: accent }}>
          <Sparkles className="w-3.5 h-3.5" /> Plan this trip with AI — free
        </a>
      </div>

      <p className="mt-auto pt-3 text-[9px] text-zinc-400 text-center">Powered by IndianAirports.com × TravelerPulse.pro</p>
    </div>
  );
}
